import React, { useMemo, useState, useEffect } from 'react'
import { useTable, useSortBy } from 'react-table'

import Icon from '@mui/material/Icon'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { Skeleton, TablePagination } from '@mui/material'
import RecruitmentPagination from 'module/recruitment/reusable/RecruitmentPagination'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

const ExternalTalentTable = ({
  data,
  column,
  stickyHeader,
  nav,
  onClick,
  sx,
  sxHeader,
  sxCell,
  sxNav,
  allColumnSorted = true,
  count,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  pagination = false,
  isLoading,
  height,
  ...rest
}) => {
  const tableHeader = useMemo(() => column, [column])
  const tableInstance = useTable(
    {
      columns: tableHeader,
      data: data,
      sortTypes: {
        alphanumeric: (prev, curr, columnId) => {
          if (Number.isInteger(prev?.original[columnId])) {
            if (prev?.original[columnId] > curr?.original[columnId]) {
              return 1
            } else if (prev?.original[columnId] < curr?.original[columnId]) {
              return -1
            } else {
              return 0
            }

            // if the data is and true or false data type
          } else if (typeof prev.original[columnId] === 'boolean') {
            if (prev.original[columnId] === true && curr.original[columnId] === false) {
              return 1
            } else if (prev.original[columnId] === false && curr.original[columnId] === true) {
              return -1
            } else {
              return 0
            }

            // if the data type is a string
          } else {
            if (prev?.original[columnId]?.toLowerCase() > curr?.original[columnId]?.toLowerCase()) {
              return 1
            } else if (
              prev?.original[columnId]?.toLowerCase() < curr?.original[columnId]?.toLowerCase()
            ) {
              return -1
            } else {
              return 0
            }
          }
        },
      },
    },
    useSortBy
  )
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance
  return (
    <>
      <TableContainer
        className="scrollbar"
        sx={{
          maxHeight: { height },
          overflow: 'auto',
          '& th.MuiTableCell-root': {},
          '& td.MuiTableCell-root': {
            padding: '0px',
          },
          ...sx,
        }}
        {...rest}
      >
        <Table stickyHeader={stickyHeader} {...getTableProps}>
          <TableHead sx={{}}>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <TableCell
                    align="center"
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'light-gray-text.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        ...sxHeader,
                      }}
                    >
                      {column.render('Header')}
                      {allColumnSorted && (
                        <>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon sx={{ fontSize: '32px', color: 'alternate.main' }}>
                                arrow_drop_up
                              </Icon>
                            ) : (
                              <Icon sx={{ fontSize: '32px', color: 'alternate.main' }}>
                                arrow_drop_down
                              </Icon>
                            )
                          ) : (
                            <Icon
                              sx={{
                                fontSize: '32px',
                                color: 'gray-inactive-button.main',
                              }}
                            >
                              arrow_drop_down
                            </Icon>
                          )}
                        </>
                      )}
                    </Typography>
                  </TableCell>
                ))}
                {nav && (
                  <TableCell>
                    <Typography></Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableHead>
          {isLoading ? (
            <TableBody {...getTableBodyProps()}>
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, index) => (
                    <TableCell
                      align="center"
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      <Box padding={2}>
                        <Skeleton variant="rounded" height="21px" width="80%" />
                      </Box>
                    </TableCell>
                  ))}
                  {nav && (
                    <TableCell>
                      <Typography></Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, index) => (
                    <TableCell
                      align="center"
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      <Box padding={2}>
                        <Skeleton variant="rounded" height="21px" width="80%" />
                      </Box>
                    </TableCell>
                  ))}
                  {nav && (
                    <TableCell>
                      <Typography></Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, index) => (
                    <TableCell
                      align="center"
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      <Box padding={2}>
                        <Skeleton variant="rounded" height="21px" width="80%" />
                      </Box>
                    </TableCell>
                  ))}
                  {nav && (
                    <TableCell>
                      <Typography></Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, index) => (
                    <TableCell
                      align="center"
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      <Box padding={2}>
                        <Skeleton variant="rounded" height="21px" width="80%" />
                      </Box>
                    </TableCell>
                  ))}
                  {nav && (
                    <TableCell>
                      <Typography></Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, index) => (
                    <TableCell
                      align="center"
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      <Box padding={2}>
                        <Skeleton variant="rounded" height="21px" width="80%" />
                      </Box>
                    </TableCell>
                  ))}
                  {nav && (
                    <TableCell>
                      <Typography></Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row)
                return (
                  <TableRow {...row.getRowProps()}>
                    {row.cells.map((cell, index) => {
                      return (
                        <TableCell {...cell.getCellProps()}>
                          <Box
                            sx={{
                              padding: '18px 30px',
                              ...sxCell,
                            }}
                          >
                            {cell.render('Cell')}
                          </Box>
                        </TableCell>
                      )
                    })}
                    {nav && (
                      <TableCell align="right">
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: 'alternate.main',
                            cursor: 'pointer',
                            padding: '18px 30px',
                            ...sxNav,
                          }}
                          onClick={() => onClick(row.original)}
                        >
                          {nav}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          )}
        </Table>
        {!(rows.length > 0) && !isLoading && (
          <AdxEmptyStateV2
            icon={<Icon>person_outline_outlined</Icon>}
            title="Your external talent pool is empty"
            subtitle="Talents that pass resume parsing will appear here"
          />
        )}
      </TableContainer>
      {pagination && (
        <Box sx={{ borderTop: 1, borderColor: 'gray-line.main' }}>
          <RecruitmentPagination
            count={count}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        </Box>
      )}
    </>
  )
}

export default ExternalTalentTable
